import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { IBasicStyledProps } from '@notino/react-styleguide-v20';

import { IGiftPackagingBoxSettings } from '@model';
import { GridDivider } from '@sharedComponents/Separators';
import { GeneralTextService, ServiceItem } from '@sharedComponents/Service';
import { ReactFCV } from '@types';
import { nonAwaitedPromiseHandlerParametrized } from '@utils/async';
import { DIVIDER_COLOR, DIVIDER_HEIGHT } from '@utils/constants';
import { arrayEquals } from '@utils/general';
import { CypressTestNamespace } from '@utils/test';

import { ECartPageState, IRemoveGiftPackagingAction } from '../../../context';
import { messages as generalCartMessages } from '../../../messages';
import { IEcoPackaging, IGiftPackaging } from '../../../model';
import { messages } from '../messages';
import { getGiftPackagingModel } from '../utils';

import { GiftPackagingDescription } from './GiftPackagingDescription';
import { GiftPackagingImages } from './GiftPackagingImages/GiftPackagingImages';
import { GiftPackagingUnavailable } from './GiftPackagingUnavailable';

export interface IGiftPackagingServiceProps extends IBasicStyledProps {
  giftPackagingBoxes: IGiftPackaging[];
  giftPackagingTypes: IGiftPackagingBoxSettings[];
  giftPackagingOn: boolean;
  giftPackagingPrice: number;
  ecoPackaging?: IEcoPackaging | null;
  removeGiftPackaging: (data: IRemoveGiftPackagingAction) => Promise<boolean>;
  setCartPageState: (data: ECartPageState) => void;
  isDualPriceLayout: boolean;
  showServiceInfoModal: boolean;
  giftPackagingDetailLink: string;
}

const arePropertiesEqual = (prevProps: IGiftPackagingServiceProps, nextProps: IGiftPackagingServiceProps): boolean =>
  prevProps.giftPackagingPrice === nextProps.giftPackagingPrice &&
  prevProps.ecoPackaging === nextProps.ecoPackaging &&
  arrayEquals(prevProps.giftPackagingBoxes, nextProps.giftPackagingBoxes) &&
  arrayEquals(prevProps.giftPackagingTypes, nextProps.giftPackagingTypes) &&
  prevProps.removeGiftPackaging === nextProps.removeGiftPackaging &&
  prevProps.setCartPageState === nextProps.setCartPageState &&
  prevProps.giftPackagingOn === nextProps.giftPackagingOn &&
  prevProps.isDualPriceLayout === nextProps.isDualPriceLayout;

export const GiftPackagingService: ReactFCV<IGiftPackagingServiceProps> = React.memo(
  ({
    giftPackagingBoxes,
    giftPackagingTypes,
    giftPackagingPrice,
    ecoPackaging,
    removeGiftPackaging,
    setCartPageState,
    giftPackagingOn,
    isDualPriceLayout,
    showServiceInfoModal,
    giftPackagingDetailLink,
  }) => {
    const { formatMessage } = useIntl();

    const model = getGiftPackagingModel(
      giftPackagingOn,
      giftPackagingBoxes,
      giftPackagingTypes,
      giftPackagingPrice,
      ecoPackaging,
    );

    const onChangeCheckboxGiftPackaging = useCallback(() => {
      if (giftPackagingBoxes.length !== 0) {
        nonAwaitedPromiseHandlerParametrized(removeGiftPackaging, { service: giftPackagingBoxes });
        return;
      }

      setCartPageState(ECartPageState.GIFT_PACKAGING);
    }, [removeGiftPackaging, setCartPageState, giftPackagingBoxes]);

    const onChangeBodyGiftPackaging = useCallback(() => {
      setCartPageState(ECartPageState.GIFT_PACKAGING);
    }, [setCartPageState]);

    return (
      <>
        <GridDivider color={DIVIDER_COLOR} height={DIVIDER_HEIGHT} />
        <ServiceItem
          data-testid="gift-packaging-service"
          serviceActive={model.checked}
          serviceDisabled={model.disabled}
          price={model.price}
          isDualPriceLayout={isDualPriceLayout}
          title={
            <GeneralTextService
              title={formatMessage(messages.giftWrappingTitle)}
              infoMarkText={formatMessage(messages.serviceLegalInfo)}
              titleExtended={formatMessage(messages.giftWrappingTitleExtended)}
            />
          }
          pricePerPiece={giftPackagingPrice}
          pieces={model.quantity}
          unit={formatMessage(generalCartMessages.pieceUnit)}
          onClick={onChangeCheckboxGiftPackaging}
          onBodyClick={onChangeBodyGiftPackaging}
          infoText={
            <GiftPackagingDescription
              disabled={model.disabled}
              showServiceInfoModal={showServiceInfoModal}
              linkUrl={giftPackagingDetailLink}
            />
          }
          freePriceText={formatMessage(messages.forFreePriceText)}
          additionalInfoComponent={model.checked ? <GiftPackagingImages images={model.images} /> : null}
          dataTestId={CypressTestNamespace.ECartPageServices.GIFT_PACKAGING_SERVICE}
          itemPriceDataTestId={CypressTestNamespace.ECartPageServices.GIFT_PACKAGING_SERVICE_ITEM_PRICE}
          quantityDataTestId={CypressTestNamespace.ECartPageServices.GIFT_PACKAGING_SERVICE_QUANTITY}
          totalPriceDataTestId={CypressTestNamespace.ECartPageServices.GIFT_PACKAGING_SERVICE_TOTAL_PRICE}
        />
        {model.unavailable && <GiftPackagingUnavailable />}
      </>
    );
  },
  arePropertiesEqual,
);
